import { InjectionToken, Signal, WritableSignal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { IdName, PRODUCT_LIST_SORTING_OPTIONS } from '@models';

import { Observable } from 'rxjs';

export type ExtraFilterControlName = 'priceFrom' | 'priceTo';
export type FilterControlName = 'brandIds' | 'gender' | 'sortBy' | 'category' | 'color' | 'isAvailablePointsRange';

export type AvailableQueryFilters = FilterControlName | 'itemsLoaded' | 'scroll';
export const FILTER_CONTROL_NAMES: FilterControlName[] = [
  'brandIds',
  'gender',
  'sortBy',
  'category',
  'color',
  'isAvailablePointsRange',
];
export const QUERY_LIST_PARAMS: AvailableQueryFilters[] = ['itemsLoaded', 'scroll'];
export const AVAILABLE_QUERY_FILTERS: AvailableQueryFilters[] = [...FILTER_CONTROL_NAMES, ...QUERY_LIST_PARAMS];

export type FilterOption<T = any> = { value: T; label: string; ariaLabel?: string };

export type SignalFilterOptions = Signal<Array<FilterOption>> | Signal<boolean>;

export type ProductListFilterOptions<T> = Record<FilterControlName, T>;
export type ProductListFilterOptionsSignal = ProductListFilterOptions<SignalFilterOptions>;

export type ProductListMobileTemplateType = 'filter' | 'sort';

export type FilterOptionsResponse = {
  categories: IdName[];
  brands: IdName[];
};

export type FilterOptions = {
  categories: FilterOption[];
  brands: FilterOption[];
};

export interface IFilterOptionsDataSource {
  categories: WritableSignal<FilterOption[]>;
  brands: WritableSignal<FilterOption[]>;
  genders: WritableSignal<FilterOption[]>;
  sortBy: WritableSignal<FilterOption[]>;
  isCategoriesLoading: WritableSignal<boolean>;
  isBrandsLoading: WritableSignal<boolean>;
  isGendersLoading: WritableSignal<boolean>;
  isLoading: Signal<boolean>;
  activeFilters: WritableSignal<FilterControlName[]>;
  categoriesMap: Signal<Record<string, FilterOption['value']>>;
  brandsMap: Signal<Record<string, FilterOption['value']>>;
  initialLoading: Signal<boolean>;

  setBrands: (brands: number[] | string[]) => void;
  setCategories: (categories: number[] | string[]) => void;
  setFilters: (filters: { brands: number[] | string[]; categories: number[] | string[] }) => void;
  setKeyword: (keyword: string) => void;
  setIsPromo: (isPromo: boolean) => void;
  resetSelectedFilters: () => void;
  fetchOptions: () => Observable<FilterOptions>;
}
export const FILTER_OPTIONS_SERVICE = new InjectionToken<IFilterOptionsDataSource>('FILTER_OPTIONS_SERVICE');

export const ARIA_LABEL_BY_FILTER: Record<FilterControlName, string> = {
  brandIds: 'Filter by brands',
  sortBy: 'Sorting combobox',
  gender: 'Filter by gender',
  category: 'Filter by category',
  color: 'Filter by color',
  isAvailablePointsRange: 'Show items in my {rewardName} range',
};
export const ID_BY_FILTER: Record<FilterControlName, string> = {
  brandIds: 'products_list_filter_brancdIds',
  sortBy: 'products_list_filter_sortBy',
  gender: 'products_list_filter_gender',
  category: 'products_list_filter_category',
  color: 'products_list_filter_color',
  isAvailablePointsRange: 'products_list_filter_isAvailablePointsRange',
};

export const LABEL_BY_FILTER: Record<FilterControlName, string> = {
  brandIds: 'Filter by:',
  sortBy: 'Sort by:',
  gender: 'Filter by:',
  category: 'Filter by:',
  color: 'Filter by:',
  isAvailablePointsRange: 'Only show items in my {rewardName} range',
};

export interface ProductListFiltersForm {
  brandIds: FormControl<string[] | number[]>;
  gender: FormControl<string>;
  sortBy: FormControl<string>;
  category: FormControl<string[] | number[]>;
  color: FormControl<string>;
  isAvailablePointsRange: FormControl<boolean>;
}

export type FormState = {
  brandIds?: string[] | number[];
  category?: string[] | number[];
  isAvailablePointsRange?: boolean;
  sortBy?: string;
  gender?: string;
  color?: string;
};

export const DEFAULT_FILTERS_FORM_STATE: FormState = {
  brandIds: [],
  category: [],
  isAvailablePointsRange: false,
  sortBy: PRODUCT_LIST_SORTING_OPTIONS[0].value,
};
